import {
  Archive,
  ArrowRight,
  Calendar,
  CheckCircle2,
  CircleDot,
  Flag,
  Layers3,
  ListChecks,
  Plus,
  Target,
  Trash2
} from "lucide-react";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ProjectCard, TaskRow } from "../components/records";
import { Button, Card, EmptyState, PageHeader, SectionHeading, StatusPill } from "../components/ui";
import { useApp } from "../context/AppContext";
import { formatDate, statusLabels } from "../lib/format";
import { api } from "../lib/api";

type Tab = "projects" | "goals" | "tasks";

export function ProjectsPage() {
  const { data, openModal, mutate } = useApp();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>(() => {
    const requested = searchParams.get("tab");
    return requested === "tasks" || requested === "goals" ? requested : "projects";
  });
  const [taskFilter, setTaskFilter] = useState("OPEN");
  if (!data) return null;

  const filteredTasks = useMemo(
    () =>
      data.tasks.filter((task) => {
        if (taskFilter === "ALL") return true;
        if (taskFilter === "DONE") return task.status === "COMPLETED";
        return !["COMPLETED", "CANCELLED", "ARCHIVED"].includes(task.status);
      }),
    [data.tasks, taskFilter]
  );

  return (
    <div className="page page--projects">
      <PageHeader
        eyebrow="GOALS & PROJECTS · 执行引擎"
        title="目标与项目"
        description="目标决定方向，项目把方向变成结果。"
        actions={
          <div className="button-group">
            <Button variant="secondary" onClick={() => openModal("goal")} icon={<Target size={16} />}>
              新目标
            </Button>
            <Button onClick={() => openModal("project")} icon={<Plus size={16} />}>
              新项目
            </Button>
          </div>
        }
      />

      <div className="tabs" role="tablist" aria-label="目标与项目视图">
        <button role="tab" aria-selected={tab === "projects"} onClick={() => setTab("projects")} className={tab === "projects" ? "active" : ""}>
          <Layers3 size={16} /> 项目 <span>{data.projects.length}</span>
        </button>
        <button role="tab" aria-selected={tab === "goals"} onClick={() => setTab("goals")} className={tab === "goals" ? "active" : ""}>
          <Target size={16} /> 目标 <span>{data.goals.length}</span>
        </button>
        <button role="tab" aria-selected={tab === "tasks"} onClick={() => setTab("tasks")} className={tab === "tasks" ? "active" : ""}>
          <ListChecks size={16} /> 任务 <span>{data.tasks.length}</span>
        </button>
      </div>

      {tab === "projects" && (
        <>
          <div className="project-overview">
            <OverviewItem
              icon={<CircleDot size={18} />}
              label="推进中"
              value={data.projects.filter((item) => item.status === "ACTIVE").length}
            />
            <OverviewItem
              icon={<Flag size={18} />}
              label="需关注"
              value={data.projects.filter((item) => item.risk !== "NONE").length}
            />
            <OverviewItem
              icon={<CheckCircle2 size={18} />}
              label="已完成"
              value={data.projects.filter((item) => item.status === "COMPLETED").length}
            />
            <OverviewItem
              icon={<Archive size={18} />}
              label="未定义里程碑"
              value={data.projects.filter((item) => item.progress === null).length}
            />
          </div>
          {data.projects.length ? (
            <div className="project-grid">
              {data.projects.map((project) => <ProjectCard key={project.id} project={project} />)}
              <button className="project-add-card" onClick={() => openModal("project")}>
                <span><Plus size={21} /></span>
                <strong>开始一个新项目</strong>
                <small>先定义结果，再安排任务</small>
              </button>
            </div>
          ) : (
            <Card>
              <EmptyState
                icon={<Layers3 size={22} />}
                title="还没有正在推进的项目"
                description="项目不是长期职责，而是一个有明确成果和结束条件的推进单元。"
                actionLabel="创建第一个项目"
                onAction={() => openModal("project")}
              />
            </Card>
          )}
        </>
      )}

      {tab === "goals" && (
        <Card>
          <SectionHeading
            title="目标地图"
            description="目标回答“为什么值得”，项目负责把它变成结果。"
            action={<Button variant="secondary" size="sm" onClick={() => openModal("goal")} icon={<Plus size={14} />}>添加目标</Button>}
          />
          {data.goals.length ? (
            <div className="goal-list">
              {data.goals.map((goal) => {
                const projectCount = data.projects.filter((project) => project.goal_id === goal.id).length;
                return (
                  <article className="goal-row" key={goal.id}>
                    <div className="goal-row__domain"><span>{goal.domain}</span></div>
                    <div className="goal-row__body">
                      <div><h3>{goal.title}</h3><StatusPill status={goal.status}>{statusLabels[goal.status]}</StatusPill></div>
                      <p>{goal.why_important || "还没有填写这个目标为什么重要。"}</p>
                      <div className="goal-row__meta">
                        <span><Layers3 size={13} />{projectCount} 个关联项目</span>
                        <span><Calendar size={13} />{goal.deadline ? formatDate(goal.deadline) : "未设置截止日"}</span>
                        {goal.success_metric && <span><Flag size={13} />{goal.success_metric}</span>}
                      </div>
                    </div>
                    <div className="goal-row__actions">
                      <ArrowRight size={18} />
                      <button
                        className="icon-delete"
                        onClick={() => void mutate(() => api.remove("goals", goal.id), "目标已移入回收站")}
                        aria-label="删除目标"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <EmptyState
              icon={<Target size={22} />}
              title="还没有定义目标"
              description="写下真正想改变的结果、成功标准，以及它为什么重要。"
              actionLabel="创建目标"
              onAction={() => openModal("goal")}
            />
          )}
        </Card>
      )}

      {tab === "tasks" && (
        <Card>
          <SectionHeading
            title="任务池"
            description="所有任务都应通向一个明确结果。"
            action={<Button onClick={() => openModal("task")} size="sm" icon={<Plus size={14} />}>新任务</Button>}
          />
          <div className="subtabs">
            <button aria-pressed={taskFilter === "OPEN"} className={taskFilter === "OPEN" ? "active" : ""} onClick={() => setTaskFilter("OPEN")}>待推进</button>
            <button aria-pressed={taskFilter === "DONE"} className={taskFilter === "DONE" ? "active" : ""} onClick={() => setTaskFilter("DONE")}>已完成</button>
            <button aria-pressed={taskFilter === "ALL"} className={taskFilter === "ALL" ? "active" : ""} onClick={() => setTaskFilter("ALL")}>全部</button>
          </div>
          {filteredTasks.length ? (
            <div className="task-list task-list--all">
              {filteredTasks.map((task) => <TaskRow key={task.id} task={task} />)}
            </div>
          ) : (
            <EmptyState
              icon={<ListChecks size={22} />}
              title={data.tasks.length ? "这个视图里没有任务" : "任务池是空的"}
              description="下一步应该足够具体，让你坐下来就能开始。"
              actionLabel="创建任务"
              onAction={() => openModal("task")}
              compact
            />
          )}
        </Card>
      )}
    </div>
  );
}

function OverviewItem({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <Card className="overview-item">
      <span>{icon}</span>
      <div><strong>{value}</strong><small>{label}</small></div>
    </Card>
  );
}
